/**
 * CSRF handling — the single home for all CSRF logic in the frontend.
 *
 * The XSRF-TOKEN cookie is the single source of truth: Laravel re-issues it on
 * every response. Direct axios calls echo it back as X-XSRF-TOKEN automatically;
 * Inertia visits do the same via the built-in XHR client. No meta-tag sync, no
 * default X-CSRF-TOKEN header, no shared prop.
 *
 * The one gap is a 419: after long inactivity (sleep/wake) the session behind
 * the cookie has expired, and the 419 response itself does NOT carry a fresh
 * cookie (Laravel's CSRF middleware throws before it can attach one). Recovery
 * is therefore: one GET to /refresh-csrf-token — whose response re-issues the
 * cookie for the (possibly brand-new guest) session — and then transparently
 * retry the original request. The user keeps all in-progress form state and at
 * most notices a brief delay.
 */
import axios from 'axios';

// Shared in-flight refresh promise so concurrent 419s trigger one request.
let refreshInFlight = null;

/**
 * Refresh the XSRF-TOKEN cookie by hitting the guest-accessible refresh route.
 *
 * The response body is irrelevant — the fresh cookie set on the response is
 * what makes the next request pass CSRF validation again.
 *
 * @returns {Promise<boolean>} true when the cookie was refreshed, false when
 *     the request failed (server unreachable — a dead session still succeeds,
 *     it simply yields a fresh guest session).
 */
export function refreshCsrfCookie() {
    refreshInFlight ??= axios
        .get('/refresh-csrf-token')
        .then(() => true)
        .catch(() => false)
        .finally(() => {
            refreshInFlight = null;
        });

    return refreshInFlight;
}

/**
 * Transparent 419 recovery for every Inertia visit (all forms, app-wide).
 *
 * A 419 arrives without the x-inertia header, so the Inertia client fires its
 * cancelable `httpException` event instead of `error` (which only ever carries
 * validation errors). We snapshot each outgoing write visit, and on a 419 we
 * suppress Inertia's raw-HTML modal, refresh the CSRF cookie, and replay the
 * snapshot via router.visit(). The page component stays mounted, so no form
 * state is lost, and the visit's own callbacks (useForm's spinner/error-bag
 * handling) fire on the replay exactly as they would have on the original.
 *
 * Only if recovery itself fails — refresh unreachable, or the replay 419s
 * again — does the user see a toast. A replay on a dead session that passes
 * CSRF but is unauthenticated is redirected to /welkom by the backend as a
 * normal Inertia page swap; no handling is needed here for that.
 *
 * @param {import('@inertiajs/vue3').router} router The Inertia router singleton.
 * @example installInertiaCsrfRecovery(router) // once, at app boot
 */
export function installInertiaCsrfRecovery(router) {
    // The most recent outgoing write visit. Only writes are snapshotted: GETs
    // are never CSRF-validated, and skipping them prevents background GET
    // prefetches from clobbering the snapshot between a submit and its 419.
    let lastVisit = null;

    // One-shot guard: true while a replay is in flight, so a 419 on the
    // replay itself falls through to the "session expired" toast instead of
    // looping.
    let recovering = false;

    router.on('before', (event) => {
        const visit = event.detail.visit;
        if (visit.method !== 'get') {
            lastVisit = visit;
        }
    });

    router.on('httpException', (event) => {
        if (event.detail.response?.status !== 419) {
            return;
        }

        // Always suppress the raw "Page Expired" iframe modal for a 419.
        event.preventDefault();

        if (recovering || !lastVisit) {
            recovering = false;
            window.toast?.warning('Je sessie is verlopen. Log opnieuw in.');
            return;
        }

        recovering = true;

        // Strip the replay-hostile parts of the snapshot: `url` is passed as
        // the href argument, and stale completed/cancelled/interrupted flags
        // would make the new visit skip its own finish handling.
        const { url, completed, cancelled, interrupted, ...options } = lastVisit;

        refreshCsrfCookie().then((refreshed) => {
            if (!refreshed) {
                recovering = false;
                window.toast?.warning('Je sessie is verlopen. Log opnieuw in.');
                return;
            }

            router.visit(url, {
                ...options,
                // Compose (not overwrite) the visit's own onFinish so the
                // guard clears no matter how the replay ends.
                onFinish: (visit) => {
                    recovering = false;
                    options.onFinish?.(visit);
                },
            });
        });
    });
}

/**
 * The same 419 resilience for direct axios calls (modals, uploads, polling)
 * that do not go through the Inertia router.
 *
 * On a 419 the CSRF cookie is refreshed and the original request is retried
 * exactly once (tracked via a flag on the request config). Anything else, or
 * a second 419, rejects as normal so callers keep their own error handling.
 *
 * @param {import('axios').AxiosStatic} axiosInstance The shared axios instance.
 * @example installAxiosCsrfRetry(axios) // once, at app boot
 */
export function installAxiosCsrfRetry(axiosInstance) {
    axiosInstance.interceptors.response.use(undefined, async (error) => {
        const config = error.config;

        if (error.response?.status !== 419 || !config || config._csrfRetried) {
            return Promise.reject(error);
        }

        config._csrfRetried = true;

        if (!(await refreshCsrfCookie())) {
            return Promise.reject(error);
        }

        return axiosInstance(config);
    });
}

/**
 * Lightweight keep-alive: ping the refresh route every 5 minutes while a tab
 * is open. This keeps the session row alive and lets the backend
 * RefreshKeycloakToken middleware renew Keycloak tokens before they expire.
 *
 * @example startSessionKeepAlive() // once, at app boot
 */
export function startSessionKeepAlive() {
    if (window.keepAliveTimer) {
        return;
    }

    const INTERVAL_MS = 5 * 60 * 1000;

    window.keepAliveTimer = setInterval(() => {
        refreshCsrfCookie();
    }, INTERVAL_MS);
}
